import React, { useEffect, useState } from "react";
import axios, { AxiosError } from "axios";
import { apiUrl, adkUrl, API_BASE } from "./utils/api";
import { fetchDeployedAgents } from "./adk-agent-builder/api";
import { SessionMetadata, getAllSessions, deleteSession as deleteSessionData, updateSessionTitle as updateSessionTitleData, getSession, saveSession } from "./utils/sessionManager";
import styled from "styled-components";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import AdminSuiteApp from "./engazeiq-admin-suite/App";
import ChatInterface, { Attachment } from "./components/ChatInterface";
import Sidebar from "./components/Sidebar";
import Login from "./components/Login";
import ResetPassword from "./components/ResetPassword";
import CreateUser from "./components/CreateUser";
import AgentBuilderPage from "./pages/AgentBuilderPage";
import { AuthProvider, useAuth } from "./context/AuthContext";

interface Chip {
  label: string;
  action: string;
}

interface Message {
  sender: string;
  content: string | any;
  chips?: Chip[];
  type?: "transfer";
  fromAgent?: string;
  toAgent?: string;
  transferState?: "transferring" | "connected";
  image_path?: string;
  attachments?: Attachment[];
  folder_info?: {
    name: string;
    file_count: number;
  };
}

const AppContainer = styled.div`
  display: flex;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  background: #f8fafc;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FullPageFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 32px;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  margin-bottom: 16px;
  color: #333;
`;

const ModalButton = styled.button`
  background-color: #FF8000;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  margin-top: 16px;

  &:hover {
    background-color: #e67300;
  }
`;

const MainAppContent: React.FC = () => {
  const { user } = useAuth();  // Get authenticated user for Memory Bank
  const [agents, setAgents] = useState<string[]>([]);
  const [agent, setAgent] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<SessionMetadata[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasShownIngestHelp, setHasShownIngestHelp] = useState<boolean>(false);

  // Load sessions from local storage when user changes
  useEffect(() => {
    if (user?.id) {
      const loadedSessions = getAllSessions(user.id);
      setSessions(loadedSessions);
    } else {
      setSessions([]);
    }
  }, [user?.id]);

  // Load agents list and automatically start a session
  // For first-time users, start with the Greeting Agent workflow
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const [adkRes, deployed] = await Promise.all([
          axios.get(adkUrl("/list-apps")),
          fetchDeployedAgents().catch(() => []),
        ]);
        const adkList = Array.isArray(adkRes.data) ? adkRes.data : [];
        const filtered = adkList.filter((name: string) => !name.includes(":"));
        const deployedIds = deployed.map(agentInfo => agentInfo.identifier);
        const agentsList = Array.from(new Set([...filtered, ...deployedIds]));
        console.log("Agents loaded:", agentsList);
        setAgents(agentsList);

        // Check if this is the user's first login
        const greetingKey = user?.email
          ? `engazeiq_greeted_${user.email}`
          : user?.id
            ? `engazeiq_greeted_${user.id}`
            : null;
        const hasBeenGreeted = greetingKey ? localStorage.getItem(greetingKey) : null;

        if (!hasBeenGreeted && greetingKey && agentsList.includes("greetingagent_agent")) {
          // First-time user: start the Greeting Agent workflow
          console.log("First-time user detected - launching Greeting Agent workflow.");
          localStorage.setItem(greetingKey, "true");
          await startSession(
            { label: "Greeting Agent", agent: "greetingagent_agent" },
            { sendInitialMessage: true }
          );
        } else if (agentsList.includes("base_agent")) {
          console.log("Auto-initializing base_agent session (no greeting message).");
          startSession("base_agent", { sendInitialMessage: false });
        }

      } catch (err) {
        console.error("Failed to fetch agents:", err);
        setError("Failed to load agents");
      }
    };
    fetchAgents();
  }, []);
  // Start a session with agent
  const startSession = async (
    agentInfo: string | { label: string; agent: string },
    options?: { sendInitialMessage?: boolean }
  ) => {
    let agentName: string;
    let initialMessageContent: string;

    if (typeof agentInfo === 'string') {
      agentName = agentInfo;
      initialMessageContent = agentInfo;
    } else {
      agentName = agentInfo.agent;
      initialMessageContent = agentInfo.label;
    }

    console.log(`Ã°Å¸Å¸Â¢ Starting session with agent: ${agentName}`);
    try {
      const res = await axios.post(
        adkUrl(`/apps/${agentName}/users/u1/sessions`)
      );
      console.log("Ã¢Å“â€¦ Session created:", res.data);

      const newSessionId = res.data.session_id || res.data.sessionId || res.data.id;
      setAgent(agentName);
      setSessionId(newSessionId);
      setMessages([]);
      if (options?.sendInitialMessage !== false) {
        await sendMessage(initialMessageContent, undefined, {
          sessionOverride: newSessionId,
          agentOverride: agentName,
        });
      }
    } catch (err) {
      console.error("Ã¢ÂÅ’ Failed to start session:", err);
      setError("Failed to start session");
    }
  };

  const persistMessage = async (
    session: string,
    entry: { role: "user" | "assistant" | "system"; content: string },
    agentForEntry: string | null
  ) => {
    if (!session || !entry.content?.trim()) {
      return;
    }
    // Use authenticated user's email for Memory Bank storage
    const userId = user?.email || "u1";
    console.log(`[MemoryBank Debug] persistMessage: user.email=${user?.email}, userId=${userId}, session=${session}`);
    try {
      await axios.post(apiUrl(`/conversations/${session}/messages`), {
        role: entry.role,
        content: entry.content,
        user_id: userId,
        user_email: user?.email,  // For Memory Bank storage
        agent_id: entry.role === "assistant" ? agentForEntry || undefined : undefined,
      });
    } catch (err) {
      console.warn("Failed to persist conversation turn", err);
    }
  };

  // Helper function to check if message is a greeting/info request
  const isGreetingOrInfoRequest = (text: string): boolean => {
    const greetings = [
      "hi", "hello", "hey", "greetings",
      "help", "info", "about", "information",
      "what can you do", "features", "capabilities",
      "start", "begin", "intro", "introduction",
      "guide", "tutorial", "how does this work"
    ];
    const normalized = text.toLowerCase().trim();
    return greetings.some(greeting =>
      normalized === greeting ||
      normalized.startsWith(greeting + " ") ||
      normalized === greeting + "?"
    );
  };

  // Send text message with full response logging
  // Send text message with full response logging
  const sendMessage = async (
    messageContent: string | { label: string; pdf_path?: string; recipient_email?: string },
    filePath?: string,
    options?: {
      sessionOverride?: string;
      agentOverride?: string | null;
      skipPersist?: boolean;
      attachments?: Attachment[];
      folder_info?: {
        name: string;
        file_count: number;
      };
    }
  ) => {
    // Extract actual message text and optional PDF info
    const content = typeof messageContent === "string" ? messageContent : messageContent.label;
    const pdf_path = typeof messageContent === "object" ? messageContent.pdf_path : undefined;
    const recipient_email = typeof messageContent === "object" ? messageContent.recipient_email : undefined;

    const activeSessionId = options?.sessionOverride ?? sessionId;
    let activeAgent = options?.agentOverride ?? agent;

    // Route greetings and info requests to base_agent (orchestrator)
    let routedToOrchestrator = false;
    if (isGreetingOrInfoRequest(content)) {
      activeAgent = "base_agent";
      routedToOrchestrator = true;
      console.log("🎯 Routing greeting/info request to base_agent (orchestrator)");
    }

    if (!content || !activeAgent) {
      console.warn("Message not sent: missing content/agent");
      return;
    }

    // If routing to orchestrator and no session exists, start one first
    let finalSessionId = activeSessionId;
    if (routedToOrchestrator && !activeSessionId) {
      console.log("Starting base_agent session for greeting...");
      try {
        const res = await axios.post(adkUrl("/apps/base_agent/users/u1/sessions"));
        finalSessionId = res.data.session_id || res.data.sessionId || res.data.id;
        console.log("Created base_agent session:", finalSessionId);
      } catch (err) {
        console.error("Failed to create base_agent session:", err);
        return;
      }
    }

    if (!finalSessionId) {
      console.warn("Message not sent: missing sessionId");
      return;
    }
    if (isLoading) {
      setMessages((prev) => [
        ...prev,
        { sender: "System", content: "Another request is still running. Please wait a moment." },
      ]);
      return;
    }

    const isFormSubmit = typeof content === "string" && content.startsWith('{"intent":"form_submit"');
    let displayContent = isFormSubmit ? "details submitted" : content;

    if (!options?.skipPersist) {
      void persistMessage(finalSessionId, { role: "user", content: displayContent }, activeAgent);
    }

    console.log("📝 Sending message:", content, "filePath:", filePath, "pdf_path:", pdf_path, "recipient_email:", recipient_email);

    // ✅ Add user's message to chat (show only file name if file attached)
    // Remove Windows & Linux file paths from display text
    if (typeof displayContent === "string") {
      displayContent = displayContent
        // remove Windows paths like E:\folder\file.ext
        .replace(/[A-Z]:\\[^\s]+/gi, "")
        // remove Linux/Mac paths like /home/user/file.ext
        .replace(/\/[^\s]+/g, "")
        .trim();
    }

    // If attachments are present, clean up the display content to hide file URLs/details
    const attachments = options?.attachments;
    if (attachments && attachments.length > 0 && typeof displayContent === "string") {
      // Remove the auto-generated file details block from display
      displayContent = displayContent
        .replace(/I'm sharing (?:a file|\d+ files) with you\. Please analyze (?:it|them) and provide details\.\s*/g, "")
        .replace(/\n*File:[\s\S]*$/, "")
        .replace(/\n*Files:\n[\s\S]*$/, "")
        .replace(/\n*Attached files:\n[\s\S]*$/, "")
        .trim();

      // If display is empty after cleanup, show a friendly summary
      if (!displayContent) {
        displayContent = attachments.length === 1
          ? `📎 ${attachments[0].file_name}`
          : `📎 ${attachments.length} files attached`;
      }
    }


    // Check if there is an image path (either from filePath arg or inferred from content if distinct)
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp"];
    let image_path: string | undefined = undefined;
    if (filePath && imageExtensions.some(ext => filePath.toLowerCase().split('?')[0].endsWith(ext))) {
      image_path = filePath;
    }

    if (!isFormSubmit) {
      setMessages((prev) => [...prev, {
        sender: "You",
        content: displayContent,
        image_path,
        attachments: options?.attachments,
        folder_info: options?.folder_info
      }]);
    }

    // If this is an attachment-only message (no real user query), just show the files
    // without calling the agent — the file cards/previews ARE the functionality
    // Legacy: Skip empty messages unless they are specific instructions
    // This is now handled by ChatInput's new behavior
    if (!content) return;

    if (
      typeof messageContent === "string" &&
      messageContent.toLowerCase().includes("ingest resume") &&
      !hasShownIngestHelp
    ) {
      setHasShownIngestHelp(true);
      setMessages((prev) => [
        ...prev,
        {
          sender: "System",
          content:
            "Use the paperclip attachment button to pick the resume files/folder. After the upload completes, press Send (or type nothing) and I will ingest the uploaded gs:// path automatically.",
        },
      ]);
    }
    setInput("");

    try {
      const payload: any = {
        appName: activeAgent,
        userId: "u1",
        sessionId: finalSessionId,
        newMessage: {
          parts: [{ text: content }],
          role: "user",
        },
        streaming: true,
        stateDelta: {},
        pdf_path,
        recipient_email,
      };

      if (filePath) {
        payload.file_attachment_path = filePath; // Add as a top-level field for backend
      }

      // Include attachment URLs in payload for backend processing
      if (options?.attachments && options.attachments.length > 0) {
        payload.attachments = options.attachments.map(att => ({
          file_name: att.file_name,
          file_type: att.file_type,
          file_size: att.file_size,
          file_url: att.file_url,
          gcs_uri: att.gcs_uri,
          local_path: att.local_path,
        }));
      }

      console.log("📤 Payload to /chat/run_sse:", payload);

      setIsLoading(true);

      // Use API gateway's SSE endpoint for metrics tracking
      // Pass user_email as query param for evaluation
      const sseUrl = `${apiUrl("/chat/run_sse")}?user_email=${encodeURIComponent(user?.email || "user")}`;
      const res = await fetch(sseUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.status === 429) {
        setMessages((prev) => [
          ...prev,
          {
            sender: "System",
            content:
              "Rate limit hit from Vertex AI. Please wait a bit and try again.",
          },
        ]);
        setIsLoading(false);
        return;
      }

      if (!res.ok || !res.body) {
        throw new Error(`Bad response: ${res.status}`);
      }

      const textDecoder = new TextDecoder();
      const reader = res.body.getReader();
      let buffer = "";
      let accumulatedText = "";
      let finalChips: Chip[] = [];
      let finalTextReceived = false;
      let lastTransferIndex: number | null = null;
      let transferTarget: string | null = null;
      let transferSettled = false; // ensure we stop glow only once

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += textDecoder.decode(value, { stream: true });

        const events = buffer.split("\n\n");
        buffer = events.pop() || "";

        for (const evt of events) {
          const lines = evt.split("\n");
          for (const line of lines) {
            if (!line.startsWith("data:")) continue;
            const jsonStr = line.slice(5).trim();
            if (!jsonStr) continue;
            try {
              const data = JSON.parse(jsonStr);

              // Show transfer events as intermediate messages
              const parts = data?.content?.parts || [];
              for (const part of parts) {
                if (part?.functionCall?.name === "transfer_to_agent") {
                  const target = part.functionCall?.args?.agent_name || "another agent";
                  transferTarget = target;
                  // eslint-disable-next-line no-loop-func
                  setMessages((prev) => {
                    // Mark any previous transfer message as settled/connected
                    const cleaned = prev.map((m) =>
                      (m as any).type === "transfer"
                        ? { ...(m as any), transferState: "connected" as const }
                        : m
                    );
                    const idx = cleaned.length;
                    lastTransferIndex = idx;
                    return [
                      ...cleaned,
                      {
                        sender: data?.author || "System",
                        content: `Transferring to ${target}...`,
                        chips: [],
                        type: "transfer" as const,
                        fromAgent: data?.author || "System",
                        toAgent: target,
                        transferState: "transferring",
                      },
                    ];
                  });
                }
              }

              // Accumulate partial text, replace with final if provided
              const textSegments: string[] = [];
              for (const part of parts) {
                if (typeof part?.text === "string") {
                  const t = part.text;
                  // Include all text segments, including JSON payloads
                  // preprocessMessage will extract forms/chips from JSON and display them properly
                  textSegments.push(t);
                }
              }

              if (textSegments.length > 0) {
                if (data?.partial === true) {
                  if (!finalTextReceived) {
                    accumulatedText += textSegments.join("");
                  }
                } else {
                  // Final full text replaces any partial accumulation
                  accumulatedText = textSegments.join("");
                  finalTextReceived = true;
                }

                // Settle transfer glow on first text segment
                if (!transferSettled && lastTransferIndex !== null) {
                  // eslint-disable-next-line no-loop-func
                  setMessages((prev) => {
                    if (lastTransferIndex === null || !prev[lastTransferIndex]) return prev;
                    const copy = [...prev];
                    const m = copy[lastTransferIndex] as any;
                    copy[lastTransferIndex] = {
                      ...m,
                      content: `Connected to ${transferTarget || "agent"}`,
                      transferState: "connected",
                    };
                    return copy;
                  });
                  transferSettled = true;
                }
              }

              if (data?.ui?.chips && Array.isArray(data.ui.chips)) {
                finalChips = data.ui.chips as Chip[];
              }
            } catch (e) {
              console.warn("⚠️ SSE JSON parse failed for line:", line, e);
            }
          }
        }
      }

      const extractChipsFromText = (text: string): { cleaned: string; chips: Chip[] } => {
        // Extract chips from JSON but KEEP the JSON in text for preprocessMessage to extract forms
        const jsonBlockMatch = text.match(/```json\s*([\s\S]*?)```/i) || text.match(/```([\s\S]*?)```/);
        const candidate = jsonBlockMatch ? jsonBlockMatch[1] : null;
        const raw = candidate ?? text;
        const firstBrace = raw.indexOf("{");
        const lastBrace = raw.lastIndexOf("}");
        if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
          return { cleaned: text, chips: [] };
        }
        const maybeJson = raw.slice(firstBrace, lastBrace + 1);
        try {
          const parsed = JSON.parse(maybeJson);
          if (parsed?.ui?.chips && Array.isArray(parsed.ui.chips)) {
            // Extract chips but KEEP full text for preprocessMessage to extract forms
            return { cleaned: text, chips: parsed.ui.chips as Chip[] };
          }
        } catch {
          // Ignore JSON parse errors; fall through.
        }
        return { cleaned: text, chips: [] };
      };

      let reply = accumulatedText?.trim() || "No reply from agent";
      if (finalChips.length === 0 && reply) {
        const extracted = extractChipsFromText(reply);
        if (extracted.chips.length > 0) {
          finalChips = extracted.chips;
          reply = extracted.cleaned || reply;
        }
      }
      // When final text arrives, render it normally (glow stops because type not set)
      setMessages((prev) => {
        const last = prev[prev.length - 1] as any;
        if (
          last &&
          last.sender === (agent || "Agent") &&
          typeof last.content === "string" &&
          last.content.trim() === reply
        ) {
          return prev;
        }
        return [...prev, { sender: agent || "Agent", content: reply, chips: finalChips }];
      });

      if (reply === "Pending tasks have been transferred successfully") {
        setMessages((prev) => [
          ...prev,
          { sender: "System", content: "Pending tasks have been transferred successfully to the specified user." },
        ]);
      }

      if (!options?.skipPersist) {
        void persistMessage(finalSessionId, { role: "assistant", content: reply }, activeAgent);
        // Automatically save session to local storage for persistence
        if (finalSessionId && activeAgent && user?.id) {
          saveSession(user.id, finalSessionId, activeAgent, [...messages, { sender: "You", content: displayContent, image_path }, { sender: activeAgent, content: reply }]);
          setSessions(getAllSessions(user.id));
        }
      }
    } catch (err) {
      console.error("Ã¢ÂÅ’ Failed to send message:", err);
      setMessages((prev) => [...prev, { sender: agent || "Agent", content: "Ã¢ÂÅ’ Failed to get response" }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAudioUpload = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("file", file);

    setMessages((prev) => [
      ...prev,
      { sender: "System", content: "Uploading audio..." },
    ]);

    try {
      const res = await axios.post(apiUrl("/upload_audio"), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const path = res.data?.saved_to as string | undefined;
      const gcp_url = res.data?.gcp_url as string | undefined;
      if (!path) {
        throw new Error("Upload succeeded but no file_path returned");
      }

      setMessages((prev) => [
        ...prev,
        {
          sender: "System",
          content: `Uploaded audio to local: ${path} and GCP: ${gcp_url}.`,
        },
      ]);
      return path;
    } catch (err: any) {
      let errorMessage = "Audio upload failed. Please try again.";
      if (err.response?.data?.message) {
        errorMessage = `Audio upload failed: ${err.response.data.message}`;
      } else if (err.message) {
        errorMessage = `Audio upload failed: ${err.message}`;
      }
      setMessages((prev) => [
        ...prev,
        { sender: "System", content: errorMessage },
      ]);
      throw new Error("Audio upload failed");
    }
  };


  // Upload file
  const handleFileUpload = async (files: FileList): Promise<string> => {
    if (!files || files.length === 0) {
      throw new Error("No files selected");
    }

    if (files.length === 1) {
      const file = files[0];
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (fileExtension === "wav" || fileExtension === "mp3" || fileExtension === "m4a") {
        try {
          return await handleAudioUpload(file);
        } catch (error) {
          console.error("Error uploading audio file:", error);
          throw new Error("Audio upload failed");
        }
      }

      const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];
      if (fileExtension && imageExtensions.includes(fileExtension)) {
        const formData = new FormData();
        formData.append("file", file);
        // Removed "Uploading image..." system message for UI cleanliness
        try {
          const res = await axios.post(apiUrl("/upload"), formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          // The /upload endpoint returns { filename, message, local_path } or { filename, gcs_uri, signed_url }
          if (res.data.signed_url) {
            return res.data.signed_url;
          }
          const filename = res.data.filename;
          // Construct local static URL
          return `${API_BASE}/static/${filename}`;
        } catch (error) {
          console.error("Error uploading image:", error);
          setMessages((prev) => [
            ...prev,
            { sender: "System", content: "Image upload failed." },
          ]);
          throw new Error("Image upload failed");
        }
      }
    }

    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append("files", file));

    // const uploadCount = files.length;
    // setMessages((prev) => [
    //   ...prev,
    //   { sender: "System", content: `Uploading ${uploadCount} file(s)...` },
    // ]);

    try {
      const res = await axios.post(apiUrl("/upload/resume-batch"), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const prefix = res.data?.gcs_prefix as string | undefined;
      if (!prefix) {
        throw new Error("Upload succeeded but no gcs_prefix returned");
      }

      // const fileCount = res.data?.file_count ?? uploadCount;
      // setMessages((prev) => [
      //   ...prev,
      //   {
      //     sender: "System",
      //     content: `Uploaded ${fileCount} file(s) to ${prefix}. Press Send (or simply hit Enter) to start ingestion.`,
      //   },
      // ]);
      return prefix;
    } catch (err) {
      const axiosErr = err as AxiosError;
      setMessages((prev) => [
        ...prev,
        { sender: "System", content: "Upload failed. Please try again." },
      ]);
      if (axiosErr?.response?.status === 404) {
        throw new Error("Resume batch upload endpoint not available on server");
      }
      throw new Error("File upload failed");
    }
  };


  return (
    <AppContainer>
      <Sidebar
        collapsed={collapsed}
        agents={agents}
        currentAgent={agent}
        sessions={sessions}
        onLoadSession={async (session) => {
          if (!user?.id) return;
          const stored = getSession(user.id, session.session_id);
          if (stored) {
            setSessionId(stored.session_id);
            setAgent(stored.agent);
            setMessages(stored.messages || []);
          }
        }}
        onDeleteSession={(session) => {
          if (!user?.id) return;
          deleteSessionData(user.id, session.session_id);
          setSessions(getAllSessions(user.id));
          if (sessionId === session.session_id) {
            setSessionId(null);
            setMessages([]);
            setAgent(null);
          }
        }}
        onUpdateSessionTitle={(id, title) => {
          if (!user?.id) return;
          updateSessionTitleData(user.id, id, title);
          setSessions(getAllSessions(user.id));
        }}
        startSession={async (agentInfo: string | { label: string; agent: string }) => {
          if (typeof agentInfo === "string") {
            return startSession(agentInfo, { sendInitialMessage: false });
          }
          return startSession(agentInfo, { sendInitialMessage: true });
        }}
        setCollapsed={setCollapsed}
      />
      <ChatInterface
        startSession={startSession}
        agent={agent}
        sessionId={sessionId}
        messages={messages}
        setMessages={setMessages}
        input={input}
        setInput={setInput}
        sendMessage={sendMessage}
        handleFileUpload={handleFileUpload}
        handleAudioUpload={handleAudioUpload}
        error={error}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
      />
    </AppContainer>
  );
};

const App: React.FC = () => {
  React.useEffect(() => {
    const handler = (event: ErrorEvent) => {
      const msg = event?.message || "";
      if (msg.includes("ResizeObserver loop") || msg.includes("undelivered notifications")) {
        event.stopImmediatePropagation?.();
        event.preventDefault?.();
      }
    };
    window.addEventListener("error", handler);
    return () => window.removeEventListener("error", handler);
  }, []);

  React.useEffect(() => {
    // Push an initial state to the history stack to enable popstate interception
    window.history.pushState(null, "", window.location.href);

    const handlePopState = (event: PopStateEvent) => {
      // When a popstate occurs (back/forward button clicked), push the state back
      window.history.pushState(null, "", window.location.href);
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/CreateUser" element={<CreateUser />} />
          <Route
            path="/admin"
            element={
              <AuthWrapper allowedRoles={["Super Admin", "Org Admin", "Builder"]}>
                <AdminSuiteApp />
              </AuthWrapper>
            }
          />
          <Route
            path="/admin/agent-builder"
            element={
              <AuthWrapper allowedRoles={["Super Admin", "Org Admin", "Builder"]}>
                <AgentBuilderPage />
              </AuthWrapper>
            }
          />
          <Route
            path="/"
            element={
              <AuthWrapper>
                <MainAppContent />
              </AuthWrapper>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
};

// AuthWrapper component to guard routes
const AuthWrapper: React.FC<{ children: React.ReactNode; allowedRoles?: string[] }> = ({
  children,
  allowedRoles,
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <div>Loading authentication...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user && !allowedRoles.some(r => r.toLowerCase() === user.role?.toLowerCase())) {
    return (
      <ModalOverlay>
        <ModalContent>
          <ModalTitle>Access Denied</ModalTitle>
          <p>only admin can acess this panel</p>
          <ModalButton onClick={() => (window.location.href = "/")}>
            Go Home
          </ModalButton>
        </ModalContent>
      </ModalOverlay>
    );
  }

  return <>{children}</>;
};

export default App;
